import React, {useEffect, useRef, useState} from 'react'
import {
    Image,
    IconButton,
    useDisclosure,
    Flex,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types';
import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation} from 'swiper/modules'
import {ChevronLeftIcon, ChevronRightIcon} from '../../icons'
import {useReviewPhotos} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'
import WebDrawer from '../../web-drawer'

/* Components */
import ReviewCard from './review-card'

const ExpandedImage = ({selectedReview, setSelectedReview, closeOtherModals, allReviews = []}) => {
    const styles = useMultiStyleConfig('WriteReview')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const swiperRef = useRef(null)
    const triggerElementRef = useRef(null)
    const [activeReview, setActiveReview] = useState(null)
    const [initialSlide, setInitialSlide] = useState(0)

    // All reviews photos
    const allPhotos = useReviewPhotos(allReviews)

    useEffect(() => {
        if (selectedReview?.review && Object.keys(selectedReview?.review).length > 0) {
            triggerElementRef.current = selectedReview?.triggerElement || document.activeElement
            const idx = allPhotos.findIndex(
                (p) => p.review?.Id === selectedReview.review.Id && p.index === selectedReview.index
            )
            const targetIndex = idx >= 0 ? idx : 0
            setInitialSlide(targetIndex)
            setActiveReview(selectedReview.review)
            handleOnOpen()
        }
    }, [selectedReview?.review, selectedReview?.index])

    const restoreFocusToTrigger = () => {
        const triggerElement = triggerElementRef.current
        if (typeof triggerElement?.focus !== 'function') {
            return
        }

        const focusTrigger = () => {
            triggerElement.focus({preventScroll: true})
        }

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(focusTrigger)
            return
        }

        focusTrigger()
    }

    // After the drawer opens and swiper is mounted, jump to the correct slide without animation
    useEffect(() => {
        if (isOpen && swiperRef.current) {
            swiperRef.current.slideTo(initialSlide, 0)
        }
    }, [isOpen, initialSlide])

    const handleOnClose = () => {
        onClose();
        setSelectedReview({});
        restoreFocusToTrigger()
    }

    const handleOnOpen = () => {
        closeOtherModals.closeAllReviewsModal();
        onOpen();
    }

    const handleSlideChange = (swiper) => {
        const photo = allPhotos[swiper.activeIndex]
        if (photo && photo.review?.Id !== activeReview?.Id) {
            setActiveReview(photo.review)
        }
    }

    return (
        <WebDrawer
            isOpen={isOpen}
            onClose={handleOnClose}
            title={
                <FormattedMessage
                    id="pdp_reviews.submitted.photos"
                    defaultMessage="Submitted Photos"
                />
            }
            titleTextStyle="webH3"
            size="full"
            noBodyStyle={true}
        >
            <Box
                padding={{base: '24px 16px'}}
                sx={{
                    scrollbarGutter: {base: 'auto', lg: 'stable'},
                    '& .swiper-wrapper': {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100%'
                    },
                    '& .swiper-slide': {
                        width: '343px',
                        height: '350.38px',
                        gap: '12px',
                        transform: 'rotate(0deg)',
                        opacity: 1,
                        objectFit: 'contain',
                        backgroundPosition: 'center center'
                    }
                }}
            >
                <Swiper
                    slidesPerView={1}
                    initialSlide={initialSlide}
                    modules={[Navigation]}
                    navigation={{
                        nextEl: '.next-photo',
                        prevEl: '.prev-photo'
                    }}
                    onSwiper={(swiper) => { swiperRef.current = swiper }}
                    onSlideChange={handleSlideChange}
                >
                    {allPhotos.map((photo) => (
                        <SwiperSlide key={photo.key}>
                            <Image
                                cursor={'pointer'}
                                w="100%"
                                src={photo.url}
                                alt="Review image"
                                aria-label="Open review"
                            />
                        </SwiperSlide>
                    ))}
                    {allPhotos.length > 1 &&
                        <Flex __css={styles.navContainer}>
                            <IconButton
                                variant={'link'}
                                __css={styles.navButton}
                                left={0}
                                className={'prev-photo'}
                                sx={{
                                    '&:disabled': {
                                        opacity: '50%',
                                        background: 'white !important'
                                    }
                                }}
                                icon={<ChevronLeftIcon />}
                            />
                            <IconButton
                                variant={'link'}
                                __css={styles.navButton}
                                right={0}
                                className={'next-photo'}
                                sx={{
                                    '&:disabled': {
                                        opacity: '50%',
                                        background: 'white !important'
                                    }
                                }}
                                icon={<ChevronRightIcon />}
                            />
                        </Flex>
                    }
                </Swiper>
                <ReviewCard
                    key={'rc-expanded-' + activeReview?.Id}
                    review={activeReview}
                    configs={{
                        styles: {
                            container: {
                                border: 0,
                                py: {base: 6},
                                px: 0,
                                mb: {base: '56px'}
                            },
                            nickname: {
                                pb: {base: 2}
                            }
                        },
                        showMobileVersionToDesktop: true,
                        showDimensionStakedOnMobile: true,
                        showImages: false
                    }}
                />
            </Box>
        </WebDrawer>
    );
};

ExpandedImage.propTypes = {
    selectedReview: PropTypes.object,
    setSelectedReview: PropTypes.func,
    closeOtherModals: PropTypes.object,
    allReviews: PropTypes.array
};

export default ExpandedImage;
